import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface SubscriptionData {
  subscription: any;
  isLoading: boolean;
  isActive: boolean;
  isPendingPayPal: boolean;
  planType: string | null;
  status: string | null;
  error: Error | null;
}

export function useSubscription(): SubscriptionData {
  const { data, isLoading, error } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return null;
      }

      const { data: subscription, error: subError } = await supabase
        .from('subscriptions')
        .select('plan_type, is_active, status, id, user_id, created_at, payment_provider, paused_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (subError) throw subError;

      return subscription;
    },
    staleTime: 60 * 1000, // 60 seconds - subscription status doesn't change often
    gcTime: 120 * 1000, // 2 minutes
    refetchOnWindowFocus: false, // Subscription status doesn't change when window regains focus
    refetchOnMount: true,
    retry: 2,
  });

  const isActive = (data?.status === 'active' || data?.status === 'trialing' || data?.is_active === true) && !data?.paused_at;
  const isPendingPayPal = data?.payment_provider === 'paypal' && data?.status === 'pending';

  return {
    subscription: data || null,
    isLoading,
    isActive,
    isPendingPayPal,
    planType: data?.plan_type || null,
    status: data?.status || null,
    error: error as Error | null,
  };
}
